import React, { useState } from 'react';
import {theme} from '../../theme';
import { Stack, Typography, Button, Divider, useMediaQuery } from '@mui/material';
import ToggleButtons from '../CustomComponents/ToggleButtons';
import { useLogUserActionMutation } from '../../api/apiSlice';
import Filter from '../PhysicianDashboard/PeerDiscovery/KeyFilterComponents/Filter';
import PhysicianBars from '../PhysicianDashboard/PeerDiscovery/PhysicianBars';
import { getCategoriesAndColors, prepareCardsWithEntries, sortCardsByItemCost, calculateMaxTotalsForFilteredCategories, getFilteredCategoriesText } from '../../utils/cardComparisonCalculations';

function ProcedureComparisonGraph({ analysisData, selectedPhysicians, setSelectedPhysicians }) {
    const COLORS = Object.values(theme.palette.graphColors);
    const [analysisToggle, setAnalysisToggle] = useState('card_items');
    const [filterCategories, setFilterCategories] = useState([]);
    const [filterOpen, setFilterOpen] = useState(false);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [logUserAction] = useLogUserActionMutation();

    // Grab the cards
    const cards = analysisData?.cardComparisons || [];
    const dataKey = analysisToggle === 'supply_usage' ? 'usage_data' : 'item_cost_data';
    
    const { uniqueCategories, categoryColorMap } = getCategoriesAndColors(cards, COLORS);
    
    // selects correct data and then filters and sorts the category entries
    const preparedCards = prepareCardsWithEntries(cards, dataKey, filterCategories);
    
    // Sorts the cards themselves by item cost
    sortCardsByItemCost(preparedCards);
    
    // Calculate max totals using utility
    const { maxUsageTotal, maxItemCostTotal } = calculateMaxTotalsForFilteredCategories(cards, filterCategories);
    const maxTotal = analysisToggle === 'supply_usage' ? maxUsageTotal : maxItemCostTotal;

    const filteredCategoriesText = getFilteredCategoriesText(filterCategories, uniqueCategories);
    const isFiltered = filterCategories.length > 0 && filterCategories.length < uniqueCategories.length;    
    
    const handleBarClick = (physicianId) => {
        setSelectedPhysicians((prevSelected) => {
            let newSelected;
            if (prevSelected.includes(physicianId)) {
                newSelected = prevSelected.filter((id) => id !== physicianId);
            } else {
                if (prevSelected.length < 2) {
                    newSelected = [...prevSelected, physicianId];
                } else {
                    newSelected = prevSelected;
                }
            }
            
            logUserAction({
                action: 'viewed_procedure_comparison_physicians',
                data: { selected_physicians: newSelected }
            });
            
            return newSelected;
        });
    };

    return (
        <Stack className='pendo-procedure-analysis-graph' direction='row' spacing={2} width={'100%'} height={'50vh'} justifyContent={'center'} alignItems={'stretch'}>
            <Stack 
                spacing={1} 
                padding={1.5} 
                sx={{ 
                    backgroundColor: theme.palette.background.white, 
                    borderRadius: 1, 
                    boxShadow: 2, 
                    width: '100%', 
                    height: '100%',
                    overflow: 'hidden'
                }}
            >
                <ToggleButtons
                    options={[
                        { label: 'Card Items', value: 'card_items' }, 
                        { label: 'Supply Usage', value: 'supply_usage' }
                    ]}
                    toggleValue={analysisToggle}
                    setToggleValue={setAnalysisToggle}
                    logUserAction={logUserAction}
                    actionName="viewed_analysis_graph_tab"
                    actionLocation='admin_portal_procedure_comparison'
                />
                
                {/* This section is the same, make reusable? */}
                <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
                    <Stack>
                        { analysisToggle === 'supply_usage' ? (
                            <Typography variant="normal" fontSize={'14px'}> Average Used Cost per Case </Typography>
                        ) : (
                            <Typography variant="normal" fontSize={'14px'}> Card Supply Cost </Typography>
                        )}
                        <Typography variant="normal" fontSize={'12px'}> {filteredCategoriesText} </Typography>
                    </Stack>
                    {isSmallScreen ? (                
                        <Button variant="outlined" size="small" sx={{ alignSelf: 'flex-end' }} onClick={() => setFilterOpen(true)}> Key & Filter </Button>
                    ) : null}
                </Stack>
                
                <Divider />
                <PhysicianBars 
                    cards={preparedCards} 
                    maxTotal={maxTotal} 
                    categoryColorMap={categoryColorMap} 
                    isFiltered={isFiltered} 
                    handleBarClick={handleBarClick}
                    scrollable
                    selectedPhysicians={selectedPhysicians}
                />
            </Stack>
            
            {/* Filter Sidebar or Drawer on a small screen */}
            <Filter
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                categories={uniqueCategories}
                selectedCategories={filterCategories}
                setSelectedCategories={setFilterCategories}
                categoryColorMap={categoryColorMap}
                showClose={isSmallScreen}
                logUserAction={logUserAction}
                logActionLocation='admin_portal_procedure_comparison'
            />
        </Stack>
    );
}

export default ProcedureComparisonGraph;